import { Controller } from "@hotwired/stimulus"

// The creator-page mode switcher. Segmented tabs drive a turbo-frame to the
// selected mode's challenge × model table. The tabs live OUTSIDE the frame so
// they persist across swaps; this controller mirrors the active state
// client-side, shows a spinner on the loading tab, and fades the frame out then
// back in around the swap. The actual data swap is a plain turbo-frame
// navigation (data-turbo-frame + data-turbo-action="advance" on the tabs), so
// each mode is its own shareable, CDN-cacheable URL.
const FRAME_ID = "creator-matrix-frame"
const REVEAL_MS = 240

export default class extends Controller {
  static targets = ["frame", "tab"]

  declare readonly frameTarget: HTMLElement
  declare readonly hasFrameTarget: boolean
  declare readonly tabTargets: HTMLElement[]

  private loadingTab: HTMLElement | null = null
  private revealTimer?: number
  private readonly boundBefore = (event: Event) => this.onBeforeFetch(event)
  private readonly boundLoad = (event: Event) => this.onFrameLoad(event)
  private readonly boundError = (event: Event) => this.onFetchError(event)

  connect(): void {
    document.addEventListener("turbo:before-fetch-request", this.boundBefore)
    document.addEventListener("turbo:frame-load", this.boundLoad)
    document.addEventListener("turbo:fetch-request-error", this.boundError)
  }

  disconnect(): void {
    document.removeEventListener("turbo:before-fetch-request", this.boundBefore)
    document.removeEventListener("turbo:frame-load", this.boundLoad)
    document.removeEventListener("turbo:fetch-request-error", this.boundError)
    if (this.revealTimer) window.clearTimeout(this.revealTimer)
  }

  // Tab click: optimistically reflect the selection and spin the clicked tab.
  // Re-clicking the active tab is a no-op (avoids a pointless refetch).
  switch(event: Event): void {
    const tab = event.currentTarget as HTMLElement
    if (tab.getAttribute("aria-selected") === "true") {
      event.preventDefault()
      return
    }
    this.setActive(tab)
    this.setLoading(tab)
  }

  private isOurFrame(target: EventTarget | null): boolean {
    return target instanceof HTMLElement && (target === (this.hasFrameTarget ? this.frameTarget : null) || target.id === FRAME_ID)
  }

  private onBeforeFetch(event: Event): void {
    if (!this.isOurFrame(event.target)) return
    if (!this.hasFrameTarget) return
    this.frameTarget.classList.remove("is-revealing")
    this.frameTarget.classList.add("is-loading")
  }

  private onFrameLoad(event: Event): void {
    if (!this.isOurFrame(event.target)) return
    this.clearLoading()
    if (this.hasFrameTarget) {
      this.frameTarget.classList.remove("is-loading")
      this.frameTarget.classList.add("is-revealing")
      if (this.revealTimer) window.clearTimeout(this.revealTimer)
      this.revealTimer = window.setTimeout(
        () => this.frameTarget.classList.remove("is-revealing"),
        REVEAL_MS,
      )
    }
    // Re-wire lightbox / stack-group on the freshly swapped cells.
    document.dispatchEvent(new CustomEvent("arena:content-updated"))
    // Reconcile the active tab to the URL (covers back/forward and direct loads).
    this.syncActiveFromUrl()
  }

  private onFetchError(event: Event): void {
    if (!this.isOurFrame(event.target)) return
    this.clearLoading()
    if (this.hasFrameTarget) this.frameTarget.classList.remove("is-loading")
  }

  private setActive(active: HTMLElement): void {
    for (const tab of this.tabTargets) {
      tab.setAttribute("aria-selected", String(tab === active))
    }
  }

  private setLoading(tab: HTMLElement): void {
    this.clearLoading()
    tab.classList.add("cm-seg--loading")
    this.loadingTab = tab
  }

  private clearLoading(): void {
    this.loadingTab?.classList.remove("cm-seg--loading")
    this.loadingTab = null
  }

  private syncActiveFromUrl(): void {
    const mode = new URL(window.location.href).searchParams.get("mode")
    if (!mode) return
    const match = this.tabTargets.find((tab) => tab.dataset.key === mode)
    if (match) this.setActive(match)
  }
}
